import React from 'react';
import {
  CalendarToday,
  Person,
  LocationOn,
  History,
  Public,
  Edit,
  Psychology,
  School
} from '@mui/icons-material';
import { formatDate } from '../../utils/dateUtils';
import EventLayout from '../../components/events/EventLayout';
import BackButton from '../../components/events/BackButton';
import EventHero from '../../components/events/EventHero';
import ImageCarousel from '../../components/events/ImageCarousel';
import EventMeta from '../../components/events/EventMeta';
import KeyTakeaways from '../../components/events/KeyTakeaways';
import styled from 'styled-components';

const ClosingMessage = styled.div`
  background: white;
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  margin-top: 48px;
  margin-bottom: 60px;
  border: 1px solid rgba(0,0,0,0.1);

  h3 {
    font-family: var(--font-serif);
    font-size: 1.5rem;
    color: var(--color-text);
    margin-bottom: 16px;
  }

  p {
    font-size: 1rem;
    color: var(--color-text-light);
    line-height: 1.6;
    max-width: 800px;
    margin: 0 auto;
  }
`;

const Conference2 = () => {
  const eventData = {
    title: "History of International Relations in the Middle East",
    speaker: "Prof. Denis Charbit",
    position: "Specialist in Sociology and Political Science",
    date: "03/05/2025",
    time: "6:30 PM",
    location: "Room CE 1 104, EPFL",
    category: "Academic",
    description: `Join us for an exceptional conference on the history of international relations in the Middle East, presented by Prof. Denis Charbit. This conference will trace the evolution of geopolitical dynamics in the region, from the fall of the Ottoman Empire to the establishment of the State of Israel in 1948.`,
    media: [
      { src: '/events/conf2/conf1.jpg', objectFit: 'cover', objectPosition: 'center' },
      { src: '/events/conf2/conf2.jpg', objectFit: 'cover', objectPosition: 'center' },
      { src: '/events/conf2/conf3.jpg', objectFit: 'cover', objectPosition: '50% 70%' }
    ],
    keyTakeaways: [
      {
        icon: <History style={{ fontSize: '24px' }} />,
        title: 'Historical Watershed Moments',
        description: "The audience gained deep insights into pivotal moments that shaped the region, from the Sykes-Picot Agreement to the UN Partition Plan of 1947.",
        color: '#4F8EF7'
      },
      {
        icon: <Public style={{ fontSize: '24px' }} />,
        title: 'Colonial Legacy',
        description: 'Prof. Charbit expertly analyzed how European colonial policies influenced modern Middle Eastern state boundaries and political structures.',
        color: '#9C88FF'
      },
      {
        icon: <Edit style={{ fontSize: '24px' }} />,
        title: 'Diplomatic Milestones',
        description: 'Fascinating examination of key diplomatic agreements and their lasting impact on regional dynamics and international relations.',
        color: '#50E3C2'
      },
      {
        icon: <Psychology style={{ fontSize: '24px' }} />,
        title: 'Power Dynamics',
        description: 'Insightful analysis of how regional and international power dynamics evolved throughout this transformative period.',
        color: '#FF6B9D'
      },
      {
        icon: <School style={{ fontSize: '24px' }} />,
        title: 'Academic Impact',
        description: 'The session provided valuable frameworks for understanding contemporary Middle Eastern geopolitics through historical context.',
        color: '#FFB74D'
      }
    ]
  };

  const metaItems = [
    {
      icon: <Person style={{ fontSize: '24px' }} />,
      gradient: 'linear-gradient(135deg, #4F8EF7, #9C88FF)',
      title: eventData.speaker,
      subtitle: eventData.position
    },
    {
      icon: <CalendarToday style={{ fontSize: '24px' }} />,
      gradient: 'linear-gradient(135deg, #50E3C2, #4DD0E1)',
      title: `${formatDate(eventData.date)} at ${eventData.time}`,
      subtitle: 'Conference Date & Time'
    },
    {
      icon: <LocationOn style={{ fontSize: '24px' }} />,
      gradient: 'linear-gradient(135deg, #FF6B9D, #FF8A65)',
      title: eventData.location,
      subtitle: 'Venue'
    }
  ];

  return (
    <EventLayout>
      <BackButton />
      <EventHero
        category={eventData.category}
        title={eventData.title}
      >
        <EventMeta items={metaItems} />
        <p style={{
          fontSize: '18px',
          color: '#64748b',
          lineHeight: '1.7',
          marginBottom: '0'
        }}>
          {eventData.description}
        </p>
      </EventHero>

      <ImageCarousel
        media={eventData.media}
        interval={4000}
      />

      <KeyTakeaways items={eventData.keyTakeaways} />

      <ClosingMessage>
        <h3>Event Highlights & Impact</h3>
        <p>
          The conference drew over 60 attendees from various academic backgrounds, sparking engaging discussions during the Q&A session. Prof. Charbit's expertise brought clarity to complex historical developments that continue to influence contemporary Middle Eastern politics. The event fostered meaningful dialogue between students and faculty, contributing to a deeper understanding of regional dynamics.
          <br /><br />
          Special thanks to Prof. Charbit and all participants for making this event a remarkable success.
        </p>
      </ClosingMessage>
    </EventLayout>
  );
};

export default Conference2;